import { setFirmZone } from "@/lib/dt";
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { clearAppCaches, clearLocalState } from "@/lib/pwa";   // Batch 19 · Stage C

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  useEffect(() => { setFirmZone(org?.time_zone || null); }, [org]);
  const [loading, setLoading] = useState(true);

  const loadOrg = async () => {
    try {
      // RBAC pass 2026-09-14: switched from /organization (firm-admin
      // only) to /organization/public — branding subset that every
      // authenticated tenant user can safely read. Preparer/Reviewer
      // logins used to trigger a firm-admin leak on mount.
      const r = await api.get("/organization/public");
      setOrg(r.data);
    } catch { /* ignore */ }
  };

  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    const token = localStorage.getItem("af_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await loadOrg(); })
      .catch(() => { localStorage.removeItem("af_token"); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password }, { _silent: true });
    if (r.data.twofa_required) {
      // Caller is responsible for navigating to /login/verify with this data.
      return { twofa_required: true, ...r.data,
                _pending: { email, password },
                delivery_failed: !!r.data.delivery_failed };
    }
    localStorage.setItem("af_token", r.data.token);
    setUser(r.data.user);
    await loadOrg();
    return r.data.user;
  };

  const setSession = async (token, u) => {
    localStorage.setItem("af_token", token);
    setUser(u);
    await loadOrg();
    return u;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("af_token", r.data.token);
    setUser(r.data.user);
    await loadOrg();
    return r.data.user;
  };

  const logout = async () => {
    // Batch 19 · Stage C (C1): sign-out clears the app's caches (the shell caches of the installed app) and every stored value
    // of this origin — nothing of the signed-in session stays on a shared device
    localStorage.removeItem("af_token");
    setUser(null); setOrg(null);
    try { await clearAppCaches(); } catch { /* noop */ }
    clearLocalState();
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, org, loading, login, register, logout, refreshOrg: loadOrg, refreshUser, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
